import { clearPinBuryState, getPinBuryState, setPinned, setBuried } from '@/atoms/pinBury'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { groupedAtom } from '@/atoms/feed'
import { useAtomValue } from 'jotai'
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react'
import {
  ArrowDown,
  ArrowUp,
  EllipsisIcon,
  MailOpen,
  Pin,
  PinOffIcon
} from '@hugeicons/core-free-icons'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DesktopMenu } from './DesktopMenu'
import { MobileMenu } from './MobileMenu'

interface CompactModeMenuProps {
  pubkey: string
  className?: string
  isLastNoteRead?: boolean
  areAllNotesRead?: boolean
  onMarkAsUnread?: () => void
}

export default function CompactModeMenu({
  pubkey,
  className,
  isLastNoteRead = false,
  areAllNotesRead = false,
  onMarkAsUnread
}: CompactModeMenuProps) {
  const { t } = useTranslation()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const grouped = useAtomValue(groupedAtom)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const state = useMemo(() => getPinBuryState(pubkey), [pubkey])

  const closeDrawer = () => {
    setIsDrawerOpen(false)
  }

  const menuActions = useMemo(() => {
    const actions: {
      icon: IconSvgElement
      label: string
      onClick: () => void
      disabled?: boolean
      separator?: boolean
    }[] = []

    if (state === 'pinned') {
      actions.push({
        icon: PinOffIcon,
        label: t('Unpin user'),
        onClick: () => {
          closeDrawer()
          clearPinBuryState(pubkey)
        },
        disabled: false
      })
      actions.push({
        icon: ArrowDown,
        label: t('Bury user'),
        onClick: () => {
          closeDrawer()
          setBuried(pubkey)
        },
        disabled: false
      })
    } else if (state === 'buried') {
      actions.push({
        icon: ArrowUp,
        label: t('Unbury user'),
        onClick: () => {
          closeDrawer()
          clearPinBuryState(pubkey)
        },
        disabled: false
      })
      actions.push({
        icon: Pin,
        label: t('Pin user'),
        onClick: () => {
          closeDrawer()
          setPinned(pubkey)
        },
        disabled: false
      })
    } else {
      actions.push({
        icon: Pin,
        label: t('Pin user'),
        onClick: () => {
          closeDrawer()
          setPinned(pubkey)
        },
        disabled: false
      })
      actions.push({
        icon: ArrowDown,
        label: t('Bury user'),
        onClick: () => {
          closeDrawer()
          setBuried(pubkey)
        },
        disabled: false
      })
    }

    // Add "Mark as Unread" action
    const isReadStateSaved = isLastNoteRead || areAllNotesRead
    actions.push({
      icon: MailOpen,
      label: t('Mark as unread'),
      onClick: () => {
        closeDrawer()
        onMarkAsUnread?.()
      },
      disabled: !isReadStateSaved,
      separator: true
    })

    return actions
  }, [
    state,
    pubkey,
    t,
    setPinned,
    setBuried,
    clearPinBuryState,
    isLastNoteRead,
    areAllNotesRead,
    onMarkAsUnread
  ])

  const trigger = (
    <button
      className="flex items-center text-muted-foreground hover:text-foreground h-full"
      onClick={(e) => {
        e.stopPropagation()
        setIsDrawerOpen(true)
      }}
    >
      <HugeiconsIcon icon={EllipsisIcon} className="!size-5" />
    </button>
  )

  // Only show menu when grouped notes is enabled
  if (!grouped) return null

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      {isSmallScreen ? (
        <MobileMenu
          menuActions={menuActions}
          trigger={trigger}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          closeDrawer={closeDrawer}
        />
      ) : (
        <DesktopMenu menuActions={menuActions} trigger={trigger} />
      )}
    </div>
  )
}
